<template>
  <v-layout>
    <v-dialog v-model="dialog" fullscreen>
      <v-card class="grey lighten-3">
        <modal-toolbar
          :title="$t('manageProvidersModal.manageProvidersHeader')"
          :hide-save="true"
          @keydown.esc.native="closeModal"
          @close="closeModal"
        />
        <v-flex
          xs12
          lg10
          xl8
          style="margin: auto;"
          class="pb-5 px-2 pt-3"
        >
          <v-card>
            <v-card-title primary-title>
              <v-text-field
                v-model="searchQueryComputed"
                append-icon="search"
                :label="$t('common.search')"
                single-line
                hide-details
              />
              <v-spacer />
              <v-btn color="primary" :disabled="loading > 0" @click="$refs.newProviderModal.open()">
                <span v-t="'manageProvidersModal.createNewProvider'" />
              </v-btn>
            </v-card-title>
            <new-provider-modal ref="newProviderModal" @new-provider-created="onProviderCreated" />
            <v-dialog v-model="activeStatusChangeDialog" max-width="500">
              <v-card>
                <v-card-title class="headline">
                  {{ $t('manageProvidersModal.deactivateProvider') }}
                </v-card-title>
                <v-card-text>
                  {{ $t('manageProvidersModal.deactivateProviderMessage') }}
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    flat="flat"
                    @click="activeStatusChangeDialog = false"
                  >
                    {{ $t('common.cancel') }}
                  </v-btn>
                  <v-btn
                    color="primary"
                    @click="confirmProviderActiveStatusChange"
                  >
                    {{ $t('common.confirm') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-data-table
              :rows-per-page-text="$t('dataTables.rowsPerPage')"
              :rows-per-page-items="rowsPerPageOptions"
              :no-data-text="$t('dataTables.noData')"
              :loading="loading > 0"
              :headers="manageProvidersHeaders"
              :items="providersDataWithPaging.providersData"
              :pagination.sync="paginationProviders"
              :total-items="providersDataWithPaging.pagingData ? providersDataWithPaging.pagingData.totalResultsCount : 0"
            >
              <template slot="items" slot-scope="props">
                <tr>
                  <td v-if="editingProvider.id !== props.item.id" class="text-xs-left">{{ props.item.name }}</td>
                  <td v-else class="text-xs-left">
                    <v-text-field
                      ref="providerName"
                      v-model="editingProvider.name"
                      :name="$t('manageProvidersModal.providerName')"
                      :rules="[rules.lengthNotGreaterThan, rules.required]"
                    />
                  </td>
                  <td class="text-xs-center">
                    <span v-if="editingProvider.id !== props.item.id">
                      {{ props.item.active ? $t('manageProvidersModal.active') : $t('manageProvidersModal.inactive') }}
                    </span>
                    <v-layout v-else class="status-toggle-layout">
                      <div
                        class="status-toggle-click-wrapper"
                        @click="providerActiveStatusChange"
                      >
                        <v-switch
                          v-model="editingProvider.active"
                          color="primary"
                        />
                      </div>
                      <span>
                        {{ editingProvider.active ? $t('manageProvidersModal.active') : $t('manageProvidersModal.inactive') }}
                      </span>
                    </v-layout>
                  </td>
                  <td v-if="editingProvider.id !== props.item.id" class="text-xs-right">
                    <v-btn icon :disabled="loading > 0" @click="editRow(Object.assign({}, props.item))">
                      <v-icon>edit</v-icon>
                    </v-btn>
                  </td>
                  <td v-else class="text-xs-right">
                    <v-tooltip bottom :disabled="JSON.stringify(props.item) !== JSON.stringify(editingProvider)">
                      <v-btn
                        slot="activator"
                        color="primary"
                        :loading="loading > 0"
                        :disabled="JSON.stringify(props.item) === JSON.stringify(editingProvider)"
                        @click="props.item.active && !editingProvider.active ? activeStatusChangeDialog = true : updateProvider()"
                      >
                        <span v-t="'common.save'" />
                      </v-btn>
                      <span v-t="'manageProvidersModal.noChangesTooltip'" />
                    </v-tooltip>
                    <v-btn icon @click="editingProvider.id = 0">
                      <v-icon>close</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import _ from 'lodash';
import gql from 'graphql-tag';
import NotificationService from '@/services/NotificationService';
import ErrorHandler from '@/services/ErrorHandler';
import ValidationService from '@/services/ValidationService';
import NewProviderModal from './new-provider-modal/NewProviderModal.vue';
import ModalToolbar from '../edit-modal/modal-toolbar/ModalToolbar.vue';

const defaultPagination = {
  sortBy: 'Active',
  descending: true,
  page: 1,
  rowsPerPage: 10,
};

export default {
  name: 'ManageProvidersModal',
  components: {
    'new-provider-modal': NewProviderModal,
    'modal-toolbar': ModalToolbar,
  },
  data() {
    return {
      dialog: false,
      activeStatusChangeDialog: false,
      searchQuery: '',
      providersDataWithPaging: {
        providersData: [],
        pagingData: {
          totalResultsCount: 0,
        },
      },
      paginationProviders: defaultPagination,
      editingProvider: {
        id: 0,
        name: null,
        active: null,
      },
      loading: 0,
      rules: {
        required: ValidationService.rules.required(),
        lengthNotGreaterThan: ValidationService.rules.lengthNotGreaterThan('Value must not exceed 100 characters.', 100),
      },
    };
  },
  computed: {
    manageProvidersHeaders() {
      return [
        {
          text: this.$t('manageProvidersModal.providerName'),
          value: 'Name',
          align: 'left',
          sortable: true,
          width: '50%',
        },
        {
          text: this.$t('manageProvidersModal.providerStatus'),
          value: 'Active',
          align: 'center',
          sortable: true,
        },
        {
          align: 'right',
          sortable: false,
          value: 'action',
        },
      ];
    },
    searchQueryComputed: {
      get() {
        return this.searchQuery;
      },
      set: _.debounce(function setSearchQuery(newValue) {
        this.searchQuery = newValue;
      }, 500),
    },
    rowsPerPageOptions() {
      return [10, 20, 30];
    },
  },
  methods: {
    open() {
      this.dialog = true;
    },
    close() {
      this.editingProvider.id = 0;
      this.dialog = false;
      this.paginationProviders = defaultPagination;
    },
    onEditProviderClick(provider) {
      this.editingProvider = Object.assign({}, provider);
    },
    confirmProviderActiveStatusChange() {
      this.updateProvider();
      this.activeStatusChangeDialog = false;
    },
    providerActiveStatusChange() {
      this.editingProvider.active = !this.editingProvider.active;
    },
    async updateProvider() {
      const newProviderNameValid = ValidationService.validate(
        Object.keys(this.$refs)
          .filter(k => k === 'providerName')
          .map(k => this.$refs[k]),
      );

      if (newProviderNameValid) {
        try {
          this.loading = 1;
          const result = await this.$apollo.mutate({
            mutation: gql`mutation UpdateProviderDataMutation($data: ProviderDataInput!) {
              updateProviderData(data: $data) {
                id
                name
                active
              }
            }`,
            variables: {
              data: {
                id: this.editingProvider.id,
                name: this.editingProvider.name.trim(),
                active: this.editingProvider.active,
              },
            },
          });

          if (result) {
            this.editingProvider.id = 0;
          }
        } catch (error) {
          ErrorHandler.handleApolloError(error);
        } finally {
          this.loading = 0;
        }
      } else {
        setTimeout(() => {
          const invalidFieldNames = [...new Set(ValidationService.getInvalidFieldNames())];
          NotificationService.showAlert({
            message: this.$t('common.invalidFields', { fields: invalidFieldNames.join(', ') }),
            level: 'error',
          });
        });
      }
    },
    onProviderCreated(providerName) {
      NotificationService.showSnackbar({
        message: this.$t('manageProvidersModal.providerCreatedSuccess', { provider: providerName }),
        timeout: 5000,
        position: 'top',
      });

      this.$apollo.queries.providersDataWithPaging.refresh();
    },
    isClean() {
      return _.isEqual(
        this.editingProvider,
        this.providersDataWithPaging.providersData.find(item => item.id === this.editingProvider.id),
      ) || this.editingProvider.id === 0;
    },
    closeModal() {
      if (this.isClean()) {
        this.close();
      } else {
        NotificationService.showUnsavedChangesModal({
          header: 'common.discardChanges',
          body: 'common.discardChangesBody',
          agreeCallback: () => this.close(),
        });
      }
    },
    editRow(row) {
      if (this.isClean()) {
        this.editingProvider = row;
      } else {
        NotificationService.showUnsavedChangesModal({
          header: 'common.discardChanges',
          body: 'common.discardChangesBodyContinue',
          agreeCallback: () => { this.editingProvider = row; },
        });
      }
    },
  },
  apollo: {
    $loadingKey: 'loading',
    providersDataWithPaging: {
      fetchPolicy: 'network-only',
      query: gql`
        query providersDataWithPagingQuery($itemsPerPage: Int!, $offset: Int!, $sortField: String, $sortDirection: String, $searchQuery: String) {
          providersDataWithPaging(itemsPerPage: $itemsPerPage, offset: $offset, sortField: $sortField, sortDirection: $sortDirection, searchQuery: $searchQuery) {
            providersData {
              id
              name
              active
            }
            pagingData {
              totalResultsCount
              resultsInPage
            }
          }
        }
      `,
      variables() {
        return {
          itemsPerPage: this.paginationProviders.rowsPerPage,
          offset: (this.paginationProviders.page - 1) * this.paginationProviders.rowsPerPage,
          sortField: this.paginationProviders.sortBy,
          sortDirection: this.paginationProviders.descending ? 'DESC' : 'ASC',
          searchQuery: this.searchQueryComputed,
        };
      },
      skip() {
        return !this.dialog;
      },
      error(error) {
        this.providersDataWithPaging.providersData = [];
        ErrorHandler.handleApolloError(error);
      },
      result(r) {
        if (!r.loading) {
          NotificationService.clearNetworkNotification();
        }
      },
    },
  },
};
</script>

<style scoped>
.status-toggle-layout {
  display: inline-flex;
  align-items: center;
}
.status-toggle-click-wrapper {
  margin-top: 22px;
  cursor: pointer;
}
.v-input--switch {
  pointer-events: none;
}
</style>
